import React, { useEffect, useState } from "react";
import { FiSave } from "react-icons/fi";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import PageHeaderSetting from "../shared/pageHeader/PageHeaderSetting";
import PerfectScrollbar from "react-perfect-scrollbar";
import Footer from "../shared/Footer";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MASK_VALUE = "********"; // Mask değeri

const SettingOneSignalForm = () => {
  const [apiSettings, setApiSettings] = useState({
    mailjet_title: "",
    mailjet_secret: "",
    mailjet_api: "",
    s3_cdn_key: "",
    s3_cdn_secret: "",
    s3_cdn_endpoint: "",
    s3_cdn_bucket: "",
    s3_cdn_region: "",
    s3_cdn_path: "",
    paytr_merchant_id: "",
    paytr_merchant_key: "",
    paytr_merchant_salt: "",
    netgsm_username: "",
    netgsm_password: "",
    // Yeni eklenen alanlar
    onesignal_app_id: "",
    onesignal_api_key: "",
    google_index: false, // true/false
  });

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  // API verilerini ilk render’da çek
  useEffect(() => {
    fetchApiData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API'den verileri çekip, hassas alanları maskeler
  const fetchApiData = async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/api/settings/1`);
      console.log("Gelen API Data:", resp.data);
      const data = resp.data || {};

      // Eğer mailjet_secret değeri varsa, bunu maskeyle gösteriyoruz.
      if (data.mailjet_secret && data.mailjet_secret !== "") {
        data.mailjet_secret = MASK_VALUE;
      }
      // Diğer hassas alanlar için de aynı mantığı uygulayabilirsiniz.
      if (data.s3_cdn_secret && data.s3_cdn_secret !== "") {
        data.s3_cdn_secret = MASK_VALUE;
      }
      if (data.paytr_merchant_key && data.paytr_merchant_key !== "") {
        data.paytr_merchant_key = MASK_VALUE;
      }
      if (data.paytr_merchant_salt && data.paytr_merchant_salt !== "") {
        data.paytr_merchant_salt = MASK_VALUE;
      }
      if (data.netgsm_password && data.netgsm_password !== "") {
        data.netgsm_password = MASK_VALUE;
      }

      // OneSignal alanları şifreliyse maskeye çevir
      if (data.onesignal_app_id && data.onesignal_app_id !== "") {
        data.onesignal_app_id = MASK_VALUE;
      }
      if (data.onesignal_api_key && data.onesignal_api_key !== "") {
        data.onesignal_api_key = MASK_VALUE;
      }

      // google_index veritabanında 1/true ise true, aksi false
      data.google_index = data.google_index === true || data.google_index === "1";

      setApiSettings((prevState) => ({
        ...prevState,
        ...data,
      }));
    } catch (err) {
      console.error("API verisi çekme hatası:", err);
      toast.error("API ayarları alınamadı!");
    }
  };

  // Input değişikliklerini handle etme
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Eğer checkbox veya radio input'sa
    if (type === "checkbox") {
      setApiSettings((prev) => ({
        ...prev,
        [name]: checked,
      }));
    }
    // Radio: google_index → "true"/"false" string'i -> boolean
    else if (name === "google_index" && type === "radio") {
      setApiSettings((prev) => ({
        ...prev,
        google_index: value === "true",
      }));
    }
    else {
      setApiSettings((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Hassas alanlarda odaklanınca maskeyi temizle
  const handleSecretFocus = (e) => {
    if (e.target.value === MASK_VALUE) {
      e.target.value = "";
      setApiSettings((prev) => ({
        ...prev,
        [e.target.name]: "",
      }));
    }
  };

  // Kaydet butonuna basınca API'yi güncelle
  const handleSave = async () => {
    try {
      // Backend'e gönderilecek veriyi hazırlarken, eğer alan maskeyi içeriyorsa
      // yani kullanıcı o alanı değiştirmediyse, o alanı güncelleme dışında bırakıyoruz.
      const dataToSave = { ...apiSettings };

      // Şifreli alanlar
      if (dataToSave.mailjet_secret === MASK_VALUE) {
        delete dataToSave.mailjet_secret;
      }
      if (dataToSave.s3_cdn_secret === MASK_VALUE) {
        delete dataToSave.s3_cdn_secret;
      }
      if (dataToSave.paytr_merchant_key === MASK_VALUE) {
        delete dataToSave.paytr_merchant_key;
      }
      if (dataToSave.paytr_merchant_salt === MASK_VALUE) {
        delete dataToSave.paytr_merchant_salt;
      }
      if (dataToSave.netgsm_password === MASK_VALUE) {
        delete dataToSave.netgsm_password;
      }

      // OneSignal
      if (dataToSave.onesignal_app_id === MASK_VALUE) {
        delete dataToSave.onesignal_app_id;
      }
      if (dataToSave.onesignal_api_key === MASK_VALUE) {
        delete dataToSave.onesignal_api_key;
      }

      // google_index: boolean → veritabanı 1/0 veya "1"/"0"
      // eğer veritabanında da boolean alan varsa bu adım gerekmeyebilir,
      // ama integer ise:
      dataToSave.google_index = dataToSave.google_index ? 1 : 0;

      await axios.put(`${BASE_URL}/api/settings/1`, dataToSave);
      toast.success(getText("SUCCESS_API_UPDATED"));
      fetchApiData(); // Güncellenmiş verileri tekrar çek
    } catch (error) {
      console.error("API Bilgileri kaydedilemedi:", error);
      toast.error("Kayıt hatası oluştu!");
    }
  };

  return (
    <div className="content-area setting-form">
      <ToastContainer />
      <PerfectScrollbar>
        <PageHeaderSetting />
        <div className="content-area-body">
          <div className="card mb-0">
            <div className="card-body">
           

              {/* OneSignal App ID (maskeli) */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  {getText("LABEL_ONESIGNAL_APP_ID")}
                </label>
                <input
                  type="password"
                  className="form-control"
                  name="onesignal_app_id"
                  value={apiSettings.onesignal_app_id || ""}
                  onChange={handleChange}
                  onFocus={handleSecretFocus}
                  placeholder="******"
                  autoComplete="off"
                />
              </div>

              {/* OneSignal API Key (maskeli) */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  {getText("LABEL_ONESIGNAL_API_KEY")}
                </label>
                <input
                  type="password"
                  className="form-control"
                  name="onesignal_api_key"
                  value={apiSettings.onesignal_api_key || ""}
                  onChange={handleChange}
                  onFocus={handleSecretFocus}
                  placeholder="******"
                  autoComplete="off"
                />
              </div>

              {/* KAYDET BUTONU */}
              <div className="d-flex justify-content-center">
                <button className="btn btn-primary" onClick={handleSave}>
                  <FiSave className="me-2" />{" "}
                  {getText("SAVE_BTN")}
                </button>
              </div>
            </div>
          </div>
        </div>
        
      </PerfectScrollbar>
    </div>
  );
};


export default SettingOneSignalForm;
